import { useState, useCallback } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { useGraphQLClient } from "./useGraphQLClient"
import { useUser } from "./useUser"
import { InviteCodeStatus } from "../utils/constants"
import {
  UpdateInviteCodeByClientAndPractitionerDocument,
  UpdateInviteCodeByClientAndPractitionerMutation,
  UpdateInviteCodeByClientAndPractitionerMutationVariables,
  GetSchedulesForDisconnectionDocument,
  GetSchedulesForDisconnectionQuery,
  GetSchedulesForDisconnectionQueryVariables,
  InsertScheduleBlackoutDatesDocument,
  InsertScheduleBlackoutDatesMutation,
  InsertScheduleBlackoutDatesMutationVariables,
  GetInviteCodeByClientAndPractitionerDocument,
  GetInviteCodeByClientAndPractitionerQuery,
  GetInviteCodeByClientAndPractitionerQueryVariables,
} from "../generated/graphql"

dayjs.extend(utc)
dayjs.extend(timezone)

export interface DisconnectionResult {
  success: boolean
  message: string
  affectedSchedules: {
    nudgeSchedules: number
    questionnaireSchedules: number
  }
  disconnectionDate: string
  inviteCodeDetails?: {
    id: number
    code: string
    previousStatus: string
    newStatus: string
  }
}

export interface DisconnectionParams {
  clientId: string
  practitionerId: string
  disconnectionType:
    | InviteCodeStatus.CLIENT_DISCONNECTED
    | InviteCodeStatus.PRACTITIONER_DISCONNECTED
}

export interface UseDisconnectionReturn {
  disconnectClient: (
    clientId: string,
    practitionerId?: string
  ) => Promise<DisconnectionResult>
  disconnectPractitioner: (
    clientId: string,
    practitionerId?: string
  ) => Promise<DisconnectionResult>
  disconnectByIds: (params: DisconnectionParams) => Promise<DisconnectionResult>
  isLoading: boolean
  error: Error | null
  lastResult: DisconnectionResult | null
  reset: () => void
}

export const useDisconnection = (): UseDisconnectionReturn => {
  const queryClient = useQueryClient()
  const graphqlClient = useGraphQLClient()
  const { user } = useUser()

  const [lastResult, setLastResult] = useState<DisconnectionResult | null>(null)

  // Main disconnection mutation
  const disconnectionMutation = useMutation({
    mutationFn: async (params: DisconnectionParams) => {
      const { clientId, practitionerId, disconnectionType } = params
      const disconnectionDate = dayjs().utc()
      const blackoutDate = disconnectionDate.format("YYYY-MM-DD")
      const disconnectionTimestamp = disconnectionDate.toISOString()

      try {
        // Step 1: Verify the invite code exists and get current details
        const inviteCodeResponse = await graphqlClient.request<
          GetInviteCodeByClientAndPractitionerQuery,
          GetInviteCodeByClientAndPractitionerQueryVariables
        >(GetInviteCodeByClientAndPractitionerDocument, {
          client_id: clientId,
          practitioner_id: practitionerId,
        })

        if (!inviteCodeResponse.invite_codes?.length) {
          throw new Error(
            "No invite code found for this client-practitioner relationship"
          )
        }

        const existingInviteCode = inviteCodeResponse.invite_codes[0]
        const previousStatus = existingInviteCode.status

        // Step 2: Update invite code status and ended_at
        const updateInviteCodeResponse = await graphqlClient.request<
          UpdateInviteCodeByClientAndPractitionerMutation,
          UpdateInviteCodeByClientAndPractitionerMutationVariables
        >(UpdateInviteCodeByClientAndPractitionerDocument, {
          client_id: clientId,
          practitioner_id: practitionerId,
          status: disconnectionType,
          ended_at: disconnectionDate,
        })

        if (
          updateInviteCodeResponse &&
          updateInviteCodeResponse.update_invite_codes?.affected_rows === 0
        ) {
          throw new Error(
            "Failed to update invite code - no matching relationship found"
          )
        }

        const updatedInviteCode =
          updateInviteCodeResponse.update_invite_codes?.returning[0]

        // Step 3: Get all schedules for this client-practitioner relationship
        const schedulesResponse = await graphqlClient.request<
          GetSchedulesForDisconnectionQuery,
          GetSchedulesForDisconnectionQueryVariables
        >(GetSchedulesForDisconnectionDocument, {
          client_id: clientId,
          practitioner_id: practitionerId,
        })

        const nudgeScheduleIds = schedulesResponse.nudge_schedules.map(
          (schedule) => schedule.id
        )
        const questionnaireScheduleIds =
          schedulesResponse.questionnaire_schedules.map(
            (schedule) => schedule.id
          )

        // Step 4: Create blackout dates for nudge schedules
        let nudgeBlackoutCount = 0
        if (nudgeScheduleIds.length > 0) {
          const nudgeBlackoutObjects = nudgeScheduleIds.map((scheduleId) => ({
            nudge_schedules_id: scheduleId,
            blackout_date: blackoutDate,
            blackout_following: true,
          }))

          const nudgeBlackoutResponse = await graphqlClient.request<
            InsertScheduleBlackoutDatesMutation,
            InsertScheduleBlackoutDatesMutationVariables
          >(InsertScheduleBlackoutDatesDocument, {
            objects: nudgeBlackoutObjects,
          })

          nudgeBlackoutCount =
            nudgeBlackoutResponse.insert_schedule_blackout_dates
              ?.affected_rows || 0
        }

        // Step 5: Create blackout dates for questionnaire schedules
        let questionnaireBlackoutCount = 0
        if (questionnaireScheduleIds.length > 0) {
          const questionnaireBlackoutObjects = questionnaireScheduleIds.map(
            (scheduleId) => ({
              questionnaire_schedules_id: scheduleId,
              blackout_date: blackoutDate,
              blackout_following: true,
            })
          )

          const questionnaireBlackoutResponse = await graphqlClient.request<
            InsertScheduleBlackoutDatesMutation,
            InsertScheduleBlackoutDatesMutationVariables
          >(InsertScheduleBlackoutDatesDocument, {
            objects: questionnaireBlackoutObjects,
          })

          questionnaireBlackoutCount =
            questionnaireBlackoutResponse.insert_schedule_blackout_dates
              ?.affected_rows || 0
        }

        const totalBlackoutDates =
          nudgeBlackoutCount + questionnaireBlackoutCount
        const disconnectionTypeDisplay = disconnectionType.replace(/_/g, " ")

        const result: DisconnectionResult = {
          success: true,
          message: `Successfully processed ${disconnectionTypeDisplay}. Updated relationship status and created ${totalBlackoutDates} schedule blackout dates.`,
          affectedSchedules: {
            nudgeSchedules: nudgeBlackoutCount,
            questionnaireSchedules: questionnaireBlackoutCount,
          },
          disconnectionDate: disconnectionDate.toISOString(),
        }

        setLastResult(result)
        return result
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred"
        const result: DisconnectionResult = {
          success: false,
          message: `Failed to disconnect: ${errorMessage}`,
          affectedSchedules: {
            nudgeSchedules: 0,
            questionnaireSchedules: 0,
          },
          disconnectionDate: disconnectionTimestamp,
        }

        setLastResult(result)
        throw new Error(result.message)
      }
    },

    onSuccess: (result) => {
      // Invalidate relevant queries to refresh the UI
      const invalidationQueries = [
        ["invite-code-status"],
        ["GetUsersByPractitioner"],
        ["GetUser"],
        ["GetClientData"],
      ]

      invalidationQueries.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey })
      })
    },
    onError: (error) => {
      console.error("Disconnection failed:", error.message)
    },
  })

  // Convenience method for client-initiated disconnection
  const disconnectClient = useCallback(
    async (
      clientId: string,
      practitionerId?: string
    ): Promise<DisconnectionResult> => {
      const targetPractitionerId = practitionerId || user?.id

      if (!targetPractitionerId) {
        throw new Error(
          "No practitioner ID provided and no authenticated practitioner found"
        )
      }

      return disconnectionMutation.mutateAsync({
        clientId,
        practitionerId: targetPractitionerId,
        disconnectionType: InviteCodeStatus.CLIENT_DISCONNECTED,
      })
    },
    [disconnectionMutation, user?.id]
  )

  // Convenience method for practitioner-initiated disconnection
  const disconnectPractitioner = useCallback(
    async (
      clientId: string,
      practitionerId?: string
    ): Promise<DisconnectionResult> => {
      const targetPractitionerId = practitionerId || user?.id

      if (!targetPractitionerId) {
        throw new Error(
          "No practitioner ID provided and no authenticated practitioner found"
        )
      }

      return disconnectionMutation.mutateAsync({
        clientId,
        practitionerId: targetPractitionerId,
        disconnectionType: InviteCodeStatus.PRACTITIONER_DISCONNECTED,
      })
    },
    [disconnectionMutation, user?.id]
  )

  // Direct method using explicit IDs
  const disconnectByIds = useCallback(
    async (params: DisconnectionParams): Promise<DisconnectionResult> => {
      return disconnectionMutation.mutateAsync(params)
    },
    [disconnectionMutation]
  )

  const reset = useCallback(() => {
    setLastResult(null)
    disconnectionMutation.reset()
  }, [disconnectionMutation])

  return {
    disconnectClient,
    disconnectPractitioner,
    disconnectByIds,
    isLoading: disconnectionMutation.isPending,
    error: disconnectionMutation.error,
    lastResult,
    reset,
  }
}
